import { useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import type { Country } from "../../types";
import type {
  ContainerOrderLine,
  ContainerOrderDirection,
} from "../../types/containerOrders";
import { createContainerCmrPdf } from "../../utils/containerCmr";

/**
 * The container's consignment note, on the driver's own screen.
 *
 * The office has had this since the module was written — a preview rail beside
 * the container's form and a download in every row menu — and the driver, who
 * is the person that actually hands the paper over at a gate, had no way to see
 * it at all. That is the gap this closes, and it closes it with the same
 * generator: `buildContainerCmrOrder` translates a container into the shape the
 * shipment module's renderer wants, and the renderer draws it unchanged. Two
 * copies of a legal form would drift.
 *
 * Drawn on the phone rather than fetched, because there is no server-side CMR
 * generator to fetch one from — the note has always been assembled client-side
 * — and because a driver at a terminal with two bars of signal is better served
 * by a document their phone can produce from data it already holds.
 *
 * Available for the whole run and never gated. The note travels *with* the
 * goods and is handed over at loading, so a version that only appeared once the
 * box was loaded would appear one stop after it was first needed. A container
 * that is still half described prints half a note, which is exactly what the
 * paper form does and is why `buildContainerCmrOrder` resolves every field to a
 * blank rather than refusing.
 *
 * Opened rather than downloaded. A phone's downloads folder is where a document
 * goes to be lost; a tab is where it goes to be shown to somebody, which is the
 * entire reason a driver wants it.
 */
interface DriverCmrTileProps {
  line: ContainerOrderLine;
  /**
   * The order's direction, and the only thing this needs from the order.
   *
   * It decides which leg of the run is the consignment: outbound loads at the
   * client and returns full, so the carriage is loading site → drop-off;
   * inbound collects full and unloads at the client, so it is the reverse.
   * Absent, the note is drawn end to end — the container's itinerary rather
   * than its consignment, which is honest but is not what the box is for.
   */
  direction?: ContainerOrderDirection | null;
  /** For the note's country boxes, which store ids and print names. */
  countries: Country[];
}

export function DriverCmrTile({
  line,
  direction,
  countries,
}: DriverCmrTileProps) {
  const { t } = useTranslation("containerOrders");
  const [busy, setBusy] = useState(false);

  const open = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const bytes = await createContainerCmrPdf(
        line,
        direction ? { direction } : undefined,
        countries,
      );
      const url = URL.createObjectURL(
        new Blob([new Uint8Array(bytes)], { type: "application/pdf" }),
      );
      window.open(url, "_blank");
      /**
       * Handed to the tab, then let go of.
       *
       * Not revoked on the next line: the new tab has not finished reading the
       * blob by then and would open on nothing. Not left alive either — a
       * driver opening the note at each of six stops would otherwise hold six
       * PDFs in memory for the life of the page. A minute is far longer than a
       * tab needs and far shorter than a shift.
       */
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error(t("lineCmr.downloadFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative shrink-0 pt-1.5 pr-1.5">
      <button
        type="button"
        onClick={open}
        disabled={busy}
        title={t("lineCmr.title")}
        /* The same 60px box as a filed document, because it is the same kind of
           thing to a driver: a paper this container travels with. What sets it
           apart is the fill — solid where the others are tinted — since it is
           the one tile that is always there and the one nobody had to file. */
        className="flex h-[60px] w-[60px] flex-col items-center justify-center gap-0.5 rounded-[10px] border border-[#BA7517] bg-[#BA7517] disabled:opacity-60"
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin text-white" />
        ) : (
          <FileText className="h-4 w-4 text-white" />
        )}
        <span className="w-full truncate px-1 text-[8.5px] font-semibold leading-tight text-white">
          {t("lineCmr.spine")}
        </span>
        {/* Where the others say who filed them, this says what it is: drawn
            from the container on the spot, not a file anybody uploaded. */}
        <span className="rounded bg-white/25 px-1 text-[7.5px] font-bold uppercase leading-tight text-white">
          {t("driver.docGenerated")}
        </span>
      </button>
    </div>
  );
}

export default DriverCmrTile;
